import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquareQuote, HelpCircle, Calendar, Mail, Inbox, Wrench, ArrowRight, MapPin, TrendingUp, Layers, ShieldAlert, ShieldCheck, Send } from "lucide-react";
import { toast } from "sonner";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
    AreaChart, Area, Legend, Cell,
} from "recharts";
import { api } from "@/lib/api";

const NAVY = "#0B2545";
const GOLD = "#E0AE32";
const CITY_COLORS = ["#0B2545", "#1E3A5F", "#2E5077", "#3E668F", "#4E7CA7", "#5E92BF", "#6EA8D7", "#7EBEEF", "#8ECAF2", "#9ED6F5", "#AEE2F8", "#BEEEFB"];

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [warranty, setWarranty] = useState(null);
    const [warrantyDays, setWarrantyDays] = useState(60);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [loading, setLoading] = useState(true);
    const [unitsKpi, setUnitsKpi] = useState(null);

    useEffect(() => {
        let alive = true;
        Promise.all([
            api.get("/admin/stats").then((r) => alive && setStats(r.data)).catch(() => {}),
            api.get("/admin/analytics").then((r) => alive && setAnalytics(r.data)).catch(() => {}),
            api.get("/admin/units-kpis").then((r) => alive && setUnitsKpi(r.data)).catch(() => {}),
        ]).finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        let alive = true;
        api.get(`/admin/warranty-alerts?days=${warrantyDays}`).then((r) => alive && setWarranty(r.data)).catch(() => {});
        return () => { alive = false; };
    }, [warrantyDays]);

    async function sendWarrantyEmail() {
        setSendingEmail(true);
        try {
            const { data } = await api.post(`/admin/warranty-alerts/send-email?days=${warrantyDays}`);
            if (data?.sent) {
                toast.success(`Email enviado (${(data.totals.expiring_parts || 0) + (data.totals.expired_parts || 0)} peças).`);
            } else if (data?.reason === "smtp_not_configured") {
                toast.warning("SMTP não configurado — configure em Definições > Email antes de enviar.");
            } else {
                toast.info("Alerta processado (verifique logs).");
            }
        } catch (err) {
            toast.error("Erro a enviar email.");
        } finally {
            setSendingEmail(false);
        }
    }

    const cards = [
        { key: "testimonials", label: "Testemunhos", icon: MessageSquareQuote, to: "/admin/testimonials", color: "bg-amber-50" },
        { key: "faq_items", label: "FAQ", icon: HelpCircle, to: "/admin/faq", color: "bg-sky-50" },
        { key: "events", label: "Eventos & Marcas", icon: Calendar, to: "/admin/events", color: "bg-emerald-50" },
        { key: "cart_configurations", label: "Simulador", icon: Wrench, to: "/admin/cart-configs", color: "bg-orange-50" },
        { key: "newsletter", label: "Newsletter", icon: Mail, to: "/admin/newsletter", color: "bg-violet-50" },
        { key: "contacts", label: "Mensagens", icon: Inbox, to: "/admin/contacts", color: "bg-rose-50" },
    ];

    const tooltipStyle = { background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 };

    return (
        <div data-testid="admin-dashboard">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <span className="eyebrow">Visão geral</span>
                    <h1 className="mt-0.5 font-heading text-xl lg:text-2xl font-extrabold text-primary leading-tight">Bem-vindo de volta.</h1>
                    <p className="mt-0.5 text-xs text-muted-foreground">Gerencie o conteúdo público da NOVIFOOD a partir daqui.</p>
                </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                {cards.map((c) => (
                    <Link key={c.key} to={c.to}
                        data-testid={`stat-card-${c.key}`}
                        className="group rounded-xl border border-border bg-white p-4 transition hover:-translate-y-1 hover:shadow-lg">
                        <div className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${c.color}`}>
                            <c.icon className="h-4 w-4 text-primary" />
                        </div>
                        <div className="mt-3 font-heading text-2xl font-extrabold text-primary">
                            {loading ? <span className="inline-block h-7 w-12 rounded bg-muted animate-pulse" /> : (stats?.[c.key] ?? 0)}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">{c.label}</div>
                        <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-primary opacity-0 group-hover:opacity-100 transition">
                            Gerir <ArrowRight className="h-3 w-3" />
                        </div>
                    </Link>
                ))}
            </div>

            {/* FLEET KPIs — Unit identity system */}
            {unitsKpi && (unitsKpi.total_units > 0 || unitsKpi.recent_interventions_30d > 0) && (
                <div className="mt-6 rounded-2xl border border-border bg-white p-5" data-testid="units-kpi-widget">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div>
                            <span className="eyebrow flex items-center gap-1.5"><Wrench className="h-3 w-3" /> Frota NOVIFOOD</span>
                            <h2 className="mt-0.5 font-heading text-lg font-extrabold text-primary">Estado da produção & assistência</h2>
                            <p className="text-xs text-muted-foreground">Identidade técnica individual de cada carrinho.</p>
                        </div>
                        <Link to="/admin/equipments" className="text-xs font-bold text-secondary hover:underline flex items-center gap-1">
                            Ver todas as unidades <ArrowRight className="h-3 w-3" />
                        </Link>
                    </div>
                    <div className="mt-4 grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
                        {[
                            { key: "total_units", label: "Produzidas", color: "text-primary", value: unitsKpi.total_units },
                            { key: "active", label: "Ativas", color: "text-emerald-600", value: unitsKpi.active },
                            { key: "in_warranty", label: "Em garantia", color: "text-emerald-600", value: unitsKpi.in_warranty },
                            { key: "expiring", label: "Garantia a expirar (60d)", color: "text-amber-600", value: unitsKpi.expiring_soon },
                            { key: "expired", label: "Garantia expirada", color: "text-red-600", value: unitsKpi.expired },
                            { key: "in_service", label: "Em assistência", color: "text-blue-600", value: unitsKpi.in_service },
                        ].map((k) => (
                            <div key={k.key} className="rounded-xl border border-border bg-muted/30 p-3" data-testid={`kpi-${k.key}`}>
                                <div className={`font-heading text-2xl font-extrabold ${k.color} leading-none`}>{k.value ?? 0}</div>
                                <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">{k.label}</div>
                            </div>
                        ))}
                    </div>
                    {unitsKpi.top_replaced_components && unitsKpi.top_replaced_components.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-border">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 font-bold">
                                Componentes mais substituídos (últimos)
                            </div>
                            <ul className="flex flex-wrap gap-2">
                                {unitsKpi.top_replaced_components.map((c, i) => (
                                    <li key={i} className="inline-flex items-center gap-1.5 rounded-full bg-red-50 border border-red-100 px-3 py-1 text-xs">
                                        <span className="font-bold text-red-700">{c.count}×</span>
                                        <span className="text-primary/80">{c.name}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <div className="mt-4 text-[11px] text-muted-foreground">
                        Intervenções nos últimos 30 dias: <span className="font-bold text-primary">{unitsKpi.recent_interventions_30d}</span>
                    </div>
                </div>
            )}

            {/* WARRANTY ALERTS */}
            {warranty && (
                <div className="mt-6 rounded-2xl border border-border bg-white p-5" data-testid="warranty-widget">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="flex items-center gap-2">
                                <ShieldAlert className="h-5 w-5 text-amber-600" />
                                <h2 className="font-heading text-lg font-black text-primary">Alertas de garantia</h2>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                                Peças e carrinhos com garantia a expirar na janela seleccionada
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1 rounded-full bg-muted p-1">
                                {[30, 60].map((d) => (
                                    <button key={d} onClick={() => setWarrantyDays(d)}
                                        data-testid={`warranty-window-${d}`}
                                        className={`rounded-full px-3 py-1 text-xs font-bold transition ${warrantyDays === d ? "bg-primary text-primary-foreground" : "text-primary hover:bg-white"}`}>
                                        {d} dias
                                    </button>
                                ))}
                            </div>
                            <button onClick={sendWarrantyEmail} disabled={sendingEmail}
                                data-testid="btn-send-warranty-email"
                                className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1.5 text-xs font-bold text-primary hover:bg-muted disabled:opacity-50">
                                <Send className="h-3.5 w-3.5" /> {sendingEmail ? "A enviar…" : "Enviar email agora"}
                            </button>
                        </div>
                    </div>

                    <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <WarrantyStat label="Peças a expirar" value={warranty.totals.expiring_parts} tone="amber" icon={ShieldAlert} testid="w-stat-expiring-parts" />
                        <WarrantyStat label="Peças expiradas" value={warranty.totals.expired_parts} tone="red" icon={ShieldAlert} testid="w-stat-expired-parts" />
                        <WarrantyStat label="Carrinhos a expirar" value={warranty.totals.expiring_carts} tone="amber" icon={ShieldAlert} testid="w-stat-expiring-carts" />
                        <WarrantyStat label="Carrinhos expirados" value={warranty.totals.expired_carts} tone="red" icon={ShieldAlert} testid="w-stat-expired-carts" />
                    </div>

                    {(warranty.expiring_parts.length + warranty.expired_parts.length + warranty.expiring_carts.length + warranty.expired_carts.length) > 0 ? (
                        <div className="mt-5 grid gap-4 lg:grid-cols-2">
                            <WarrantyList title="Peças" items={[...warranty.expired_parts.map(p => ({...p, expired: true})), ...warranty.expiring_parts]} kind="part" />
                            <WarrantyList title="Carrinhos" items={[...warranty.expired_carts.map(c => ({...c, expired: true})), ...warranty.expiring_carts]} kind="cart" />
                        </div>
                    ) : (
                        <div className="mt-4 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
                            <ShieldCheck className="h-4 w-4" /> Nenhuma garantia a expirar nos próximos {warrantyDays} dias.
                        </div>
                    )}
                </div>
            )}

            {/* ANALYTICS */}
            {analytics && (
                <div className="mt-6 space-y-4" data-testid="admin-analytics">
                    <div className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-secondary" />
                        <h2 className="font-heading text-2xl font-extrabold text-primary">Analytics</h2>
                    </div>

                    <div className="grid gap-6 lg:grid-cols-2">
                        {/* Weekly evolution */}
                        <div className="rounded-2xl border border-border bg-white p-6" data-testid="chart-weekly">
                            <h3 className="font-heading text-lg font-bold text-primary">Evolução semanal (8 semanas)</h3>
                            <p className="text-xs text-muted-foreground">Pedidos do simulador, mensagens e subscrições</p>
                            <div className="mt-4 h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={analytics.weekly} margin={{ top: 6, right: 8, left: -16, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="gConfigs" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={GOLD} stopOpacity={0.6} />
                                                <stop offset="100%" stopColor={GOLD} stopOpacity={0.05} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F5" />
                                        <XAxis dataKey="week" fontSize={11} stroke="#9CA3AF" />
                                        <YAxis fontSize={11} stroke="#9CA3AF" allowDecimals={false} />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Legend wrapperStyle={{ fontSize: 12 }} />
                                        <Area type="monotone" dataKey="configs" name="Simulador" stroke={GOLD} strokeWidth={2.5} fill="url(#gConfigs)" />
                                        <Area type="monotone" dataKey="contacts" name="Mensagens" stroke={NAVY} strokeWidth={2} fillOpacity={0} />
                                        <Area type="monotone" dataKey="newsletter" name="Newsletter" stroke="#8B5CF6" strokeWidth={2} fillOpacity={0} strokeDasharray="5 4" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* By city */}
                        <div className="rounded-2xl border border-border bg-white p-6" data-testid="chart-cities">
                            <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-secondary" />
                                <h3 className="font-heading text-lg font-bold text-primary">Pedidos por cidade</h3>
                            </div>
                            <p className="text-xs text-muted-foreground">Origem dos pedidos do simulador</p>
                            <div className="mt-4 h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={analytics.by_city} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F5" horizontal={false} />
                                        <XAxis type="number" fontSize={11} stroke="#9CA3AF" allowDecimals={false} />
                                        <YAxis type="category" dataKey="city" fontSize={11} stroke="#374151" width={92} />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Bar dataKey="count" name="Pedidos" radius={[0, 6, 6, 0]}>
                                            {analytics.by_city.map((_, i) => (
                                                <Cell key={i} fill={CITY_COLORS[i % CITY_COLORS.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Top equipment */}
                        <div className="rounded-2xl border border-border bg-white p-6" data-testid="chart-addons">
                            <div className="flex items-center gap-2">
                                <Layers className="h-4 w-4 text-secondary" />
                                <h3 className="font-heading text-lg font-bold text-primary">Equipamentos mais pedidos</h3>
                            </div>
                            <div className="mt-4 h-64">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={analytics.top_addons} margin={{ top: 6, right: 8, left: -16, bottom: 24 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F5" />
                                        <XAxis dataKey="label" fontSize={10} stroke="#374151" angle={-22} textAnchor="end" interval={0} />
                                        <YAxis fontSize={11} stroke="#9CA3AF" allowDecimals={false} />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Bar dataKey="count" name="Pedidos" fill={GOLD} radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {/* Funnel + sizes */}
                        <div className="rounded-2xl border border-border bg-white p-6" data-testid="chart-funnel">
                            <h3 className="font-heading text-lg font-bold text-primary">Estado dos pedidos & tamanhos</h3>
                            <div className="mt-5 grid grid-cols-2 gap-3">
                                {(analytics.status_funnel || []).map((s) => (
                                    <div key={s.status} className="rounded-xl bg-muted p-4">
                                        <div className="font-heading text-3xl font-extrabold text-primary">{s.count}</div>
                                        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mt-1">
                                            {{ new: "Novos", contacted: "Contactados", converted: "Convertidos", archived: "Arquivados" }[s.status] || s.status}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-5 space-y-2">
                                {(analytics.top_sizes || []).map((s) => {
                                    const max = Math.max(...analytics.top_sizes.map((x) => x.count), 1);
                                    return (
                                        <div key={s.id} className="flex items-center gap-3">
                                            <span className="text-xs font-semibold text-primary w-36 truncate">{s.label}</span>
                                            <div className="flex-1 h-3 rounded-full bg-muted overflow-hidden">
                                                <div className="h-full rounded-full" style={{ width: `${(s.count / max) * 100}%`, background: NAVY }} />
                                            </div>
                                            <span className="text-xs font-bold text-primary w-8 text-right">{s.count}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-6 grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-border bg-white p-6">
                    <h3 className="font-heading text-lg font-bold">Atalhos rápidos</h3>
                    <ul className="mt-4 space-y-2 text-sm">
                        <li><Link to="/admin/faq" className="text-primary hover:underline">→ Editar FAQ pública</Link></li>
                        <li><Link to="/admin/testimonials" className="text-primary hover:underline">→ Adicionar novo testemunho</Link></li>
                        <li><Link to="/admin/events" className="text-primary hover:underline">→ Publicar caso de marca</Link></li>
                        <li><Link to="/admin/contacts" className="text-primary hover:underline">→ Ver mensagens recebidas</Link></li>
                    </ul>
                </div>
                <div className="rounded-2xl border border-border bg-primary text-white p-6">
                    <h3 className="font-heading text-lg font-bold">Como funciona</h3>
                    <p className="mt-3 text-sm text-white/75 leading-relaxed">
                        Tudo o que publicar aqui aparece imediatamente no site público. Use os comutadores de visibilidade (publicado/oculto) para controlar o que está visível sem apagar conteúdo.
                    </p>
                </div>
            </div>
        </div>
    );
}

function WarrantyStat({ label, value, tone, icon: Icon, testid }) {
    const toneClasses = tone === "red"
        ? "bg-red-50 border-red-200 text-red-700"
        : tone === "amber"
        ? "bg-amber-50 border-amber-200 text-amber-700"
        : "bg-muted border-border text-primary";
    return (
        <div className={`rounded-xl border p-3 ${toneClasses}`} data-testid={testid}>
            <div className="flex items-center justify-between">
                <Icon className="h-4 w-4 opacity-70" />
                <span className="font-heading text-2xl font-black">{value}</span>
            </div>
            <div className="mt-1 text-[11px] uppercase tracking-wider font-bold opacity-80">{label}</div>
        </div>
    );
}

function WarrantyList({ title, items, kind }) {
    if (!items || items.length === 0) return null;
    return (
        <div className="rounded-xl border border-border bg-muted/40 p-3">
            <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-2">{title}</div>
            <ul className="space-y-1.5">
                {items.slice(0, 6).map((it, idx) => {
                    const wu = it.warranty_until;
                    const label = kind === "part"
                        ? `${it.name} — ${it.cart?.serial_number}`
                        : `${it.serial_number}${it.model ? " · " + it.model : ""}`;
                    const link = kind === "part"
                        ? `/admin/equipments`
                        : `/admin/equipments`;
                    return (
                        <li key={`${it.part_id || it.cart_id}-${idx}`} className="flex items-center justify-between gap-2 text-xs">
                            <Link to={link} className="truncate text-primary hover:underline">
                                <span className="font-bold">{label}</span>
                                {it.owner?.email && <span className="text-muted-foreground"> · {it.owner.email}</span>}
                            </Link>
                            <span className={`shrink-0 rounded-full px-2 py-0.5 font-mono text-[10px] font-bold ${it.expired ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
                                {wu}
                            </span>
                        </li>
                    );
                })}
                {items.length > 6 && (
                    <li className="text-[11px] text-muted-foreground italic">+ {items.length - 6} outros…</li>
                )}
            </ul>
        </div>
    );
}
